import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Truck, KeyRound } from "lucide-react";
import { API } from "@/config";
import { useAuth } from "@/context/AuthProvider";

export const SetPassword = () => {
  const nav = useNavigate();
  const loc = useLocation();
  const { refresh } = useAuth();
  const params = new URLSearchParams(loc.search);
  const token = params.get("token");
  const isReset = params.get("mode") === "reset";
  const [state, setState] = useState({ status: "loading", email: "", name: "", role: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ status: "error", msg: isReset ? "This reset link is missing a token. Try requesting a new one." : "This link is missing a token. Ask your admin to send a new invite." });
      return;
    }
    axios.get(`${API}/auth/invite/${token}`)
      .then((r) => {
        // In reset mode, an "already_activated" account is EXPECTED (customer resetting their existing password).
        // In driver-invite mode, it means the invite has been consumed — show the "already" screen.
        if (r.data.already_activated && !isReset) {
          setState({ status: "already", email: r.data.email, name: r.data.name, role: r.data.role });
        } else {
          setState({ status: "ready", email: r.data.email, name: r.data.name, role: r.data.role });
        }
      })
      .catch((e) => setState({ status: "error", msg: e.response?.data?.detail || (isReset ? "Reset link is invalid or expired." : "Invite is invalid or expired.") }));
  }, [token, isReset]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/set-password`, { token, password });
      toast.success(isReset ? "Password reset — you're signed in ✨" : `Welcome to the team, ${state.name}!`);
      await refresh();
      // Route to the right dashboard based on the account's role.
      const home = state.role === "customer" ? "/account" : state.role === "partner" ? "/partners/dashboard" : "/employee";
      nav(home, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't set your password. Try requesting a fresh link.");
    } finally { setBusy(false); }
  };

  const Icon = isReset ? KeyRound : Truck;

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden py-16 px-4">
      <div className="glass max-w-md w-full p-8 md:p-10 relative z-10" data-testid="set-password-page">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#FFD166]/15 border border-[#FFD166]/40 flex items-center justify-center">
            <Icon className="w-5 h-5 text-[#FFD166]" />
          </div>
          <div>
            <p className="eyebrow text-[#FFD166]">{isReset ? "Password reset" : "Driver activation"}</p>
            <h1 className="font-serif text-3xl text-[#F6F1E4]">{isReset ? "Choose a new password" : "Set your password"}</h1>
          </div>
        </div>

        {state.status === "loading" && <p className="mt-6 text-[#B9C8D5] text-sm">Checking your link…</p>}

        {state.status === "error" && (
          <div className="mt-6" data-testid="invite-error">
            <p className="text-[#F6F1E4]/90">{state.msg}</p>
            <button onClick={() => nav(isReset ? "/login?role=customer" : "/login?role=employee")} className="btn-ghost mt-4">Back to sign in</button>
          </div>
        )}

        {state.status === "already" && (
          <div className="mt-6" data-testid="invite-already">
            <p className="text-[#F6F1E4]/90">Looks like <b className="text-[#FFD166]">{state.email}</b> already activated. Sign in below.</p>
            <button onClick={() => nav("/login?role=employee")} className="btn-primary mt-4 w-full">Go to sign in</button>
          </div>
        )}

        {state.status === "ready" && (
          <>
            <p className="text-sm text-[#B9C8D5] mt-3">
              {isReset ? (
                <>Hi <b className="text-[#FFD166]">{state.name || state.email}</b>! Pick a new password (8+ characters) and you're back in.</>
              ) : (
                <>Hi <b className="text-[#FFD166]">{state.name || state.email}</b>! Pick a strong password (8+ characters) and you're in.</>
              )}
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input type="email" value={state.email} readOnly className="input opacity-70" data-testid="invite-email-readonly" />
              <input required type="password" minLength={8} placeholder="New password (8+ chars)" className="input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password" />
              <input required type="password" minLength={8} placeholder="Confirm password" className="input" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="invite-password-confirm" />
              <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="invite-submit">
                {busy ? (isReset ? "Saving…" : "Activating…") : (isReset ? "Save new password" : "Activate my account ✨")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
